import { createAsyncThunk } from "@reduxjs/toolkit";
import localhost from "apis/localhost";
import {
  delegateNonVisiAxiosError,
  showVisiRequestFailed,
  VISI_REQUEST_CONFIG,
} from "slices/visi/everywhere/visiRequestError";

import { CHAT_UNAVAILABLE_MESSAGE } from "./constants";
import { ALL_PAGES_SCOPE, WRITER_CONVERSATIONS_URL } from "./endpoints";
import {
  selectWriterConversationId,
  selectWriterOpenRequestId,
  selectWriterSubject,
} from "./selectors";
import {
  isSameSubject,
  isUnsavedDocument,
  writerSubjectOfRow,
} from "./writerSubject";

const OPEN_FAILED_MESSAGE = "This chat could not be opened. Please try again.";

const OPEN_REFUSAL_MESSAGES = {
  CONVERSATION_UNAVAILABLE: CHAT_UNAVAILABLE_MESSAGE,
  FEATURE_UNAVAILABLE: "Chat history is turned off right now.",
};

const refusalOf = (error) => ({
  status: error?.response?.status ?? null,
  errorCode: error?.response?.data?.errorCode ?? null,
});

const thisPageParams = (subject) => ({
  entityType: subject.ownerType,
  entityId: subject.ownerId,
  documentId: subject.documentId,
});

export const fetchWriterConversations = createAsyncThunk(
  "visiWriter/fetchConversations",
  async ({ allPages } = {}, { dispatch, getState, rejectWithValue }) => {
    const subject = selectWriterSubject(getState());
    if (!allPages && (!subject || isUnsavedDocument(subject))) {
      return [];
    }
    try {
      const { data } = await localhost.get(WRITER_CONVERSATIONS_URL, {
        ...VISI_REQUEST_CONFIG,
        params: allPages ? { scope: ALL_PAGES_SCOPE } : thisPageParams(subject),
      });
      return Array.isArray(data) ? data : [];
    } catch (error) {
      delegateNonVisiAxiosError(error);
      showVisiRequestFailed(
        dispatch,
        "Chat history could not be loaded. Please try again."
      );
      return rejectWithValue(refusalOf(error));
    }
  }
);

export const OTHER_DOCUMENT_MESSAGE =
  "This chat belongs to another document, so it cannot be opened here.";

export const openWriterConversation = createAsyncThunk(
  "visiWriter/openConversation",
  async (
    conversationId,
    { dispatch, getState, rejectWithValue, requestId }
  ) => {
    try {
      const { data } = await localhost.get(
        `${WRITER_CONVERSATIONS_URL}/${conversationId}`,
        VISI_REQUEST_CONFIG
      );
      if (selectWriterOpenRequestId(getState()) !== requestId) {
        return rejectWithValue({ status: null, errorCode: null });
      }
      if (
        !isSameSubject(
          writerSubjectOfRow(data),
          selectWriterSubject(getState())
        )
      ) {
        showVisiRequestFailed(dispatch, OTHER_DOCUMENT_MESSAGE);
        return rejectWithValue({ status: null, errorCode: null });
      }
      return data;
    } catch (error) {
      delegateNonVisiAxiosError(error);
      const refusal = refusalOf(error);
      showVisiRequestFailed(
        dispatch,
        OPEN_REFUSAL_MESSAGES[refusal.errorCode] ?? OPEN_FAILED_MESSAGE
      );
      return rejectWithValue(refusal);
    }
  }
);

export const readWriterConversation = createAsyncThunk(
  "visiWriter/readConversation",
  async (row, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await localhost.get(
        `${WRITER_CONVERSATIONS_URL}/${row.id}`,
        VISI_REQUEST_CONFIG
      );
      return { row, detail: data };
    } catch (error) {
      delegateNonVisiAxiosError(error);
      const refusal = refusalOf(error);
      showVisiRequestFailed(
        dispatch,
        OPEN_REFUSAL_MESSAGES[refusal.errorCode] ?? OPEN_FAILED_MESSAGE
      );
      return rejectWithValue(refusal);
    }
  }
);

export const renameWriterConversation = createAsyncThunk(
  "visiWriter/renameConversation",
  async (
    { conversationId, title, previousTitle },
    { dispatch, rejectWithValue }
  ) => {
    const trimmedTitle = title.trim();
    try {
      await localhost.patch(
        `${WRITER_CONVERSATIONS_URL}/${conversationId}`,
        { title: trimmedTitle },
        VISI_REQUEST_CONFIG
      );
      return { conversationId, title: trimmedTitle };
    } catch (error) {
      delegateNonVisiAxiosError(error);
      showVisiRequestFailed(
        dispatch,
        "This chat could not be renamed. Please try again."
      );
      return rejectWithValue({ ...refusalOf(error), previousTitle });
    }
  },
  { condition: ({ title }) => Boolean(title?.trim()) }
);

export const deleteWriterConversation = createAsyncThunk(
  "visiWriter/deleteConversation",
  async (conversationId, { dispatch, getState, rejectWithValue }) => {
    try {
      await localhost.delete(
        `${WRITER_CONVERSATIONS_URL}/${conversationId}`,
        VISI_REQUEST_CONFIG
      );
      return {
        conversationId,
        replacesOpenChat:
          selectWriterConversationId(getState()) === conversationId,
        nextConversationKey: crypto.randomUUID(),
      };
    } catch (error) {
      delegateNonVisiAxiosError(error);
      showVisiRequestFailed(
        dispatch,
        "This chat could not be deleted. Please try again."
      );
      return rejectWithValue(refusalOf(error));
    }
  }
);
